import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MainNavigation from '../../components/ui/MainNavigation';
import Footer from '../home-page/components/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/profileService';
import ProfileHeader from './components/ProfileHeader';
import ProfileStatistics from './components/ProfileStatistics';
import ProfileReviews from './components/ProfileReviews';
import ProfilePhotoUpload from './components/ProfilePhotoUpload';
import ProfileEditForm from './components/ProfileEditForm';
import Button from '../../components/ui/Button';

const PublicUserProfile = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);

  const userId = searchParams?.get('userId') || user?.id;
  const isOwnProfile = user?.id === userId;

  useEffect(() => {
    if (userId) {
      fetchProfileData();
    }
  }, [userId]);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await profileService?.getUserProfile(userId);
      if (profileError) throw profileError;
      setProfile(profileData);

      // Fetch statistics
      const { data: statsData, error: statsError } = await profileService?.getUserStatistics(userId, profileData?.role);
      if (statsError) throw statsError;
      setStatistics(statsData);

      // Fetch reviews
      const { data: reviewsData, error: reviewsError } = await profileService?.getUserReviews(userId);
      if (reviewsError) throw reviewsError;
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    try {
      const { data, error } = await profileService?.uploadProfilePhoto(userId, file);
      if (error) throw error;

      // Update profile with new photo URL
      await profileService?.updateUserProfile(userId, {
        profilePhotoUrl: data?.publicUrl
      });

      // Refresh profile data
      await fetchProfileData();
      setShowPhotoUpload(false);
    } catch (error) {
      console.error('Error uploading photo:', error);
    }
  };

  const handleProfileUpdate = async (updates) => {
    try {
      const { data, error } = await profileService?.updateUserProfile(userId, updates);
      if (error) throw error;

      setProfile(data);
      setIsEditMode(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <MainNavigation />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="font-body text-muted-foreground">Loading profile...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <MainNavigation />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="font-heading text-2xl font-semibold text-foreground mb-4">Profile not found</p>
            <Button onClick={() => navigate('/')}>Return Home</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MainNavigation />
      
      <main className="flex-1 py-8 md:py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Profile Header */}
          <ProfileHeader 
            profile={profile}
            isOwnProfile={isOwnProfile}
            onEditClick={() => setIsEditMode(true)}
            onPhotoClick={() => setShowPhotoUpload(true)}
          />

          {/* Edit Mode */}
          {isEditMode && (
            <div className="mt-8">
              <ProfileEditForm
                profile={profile}
                onSave={handleProfileUpdate}
                onCancel={() => setIsEditMode(false)}
              />
            </div>
          )}

          {/* Photo Upload Modal */}
          {showPhotoUpload && (
            <ProfilePhotoUpload
              onUpload={handlePhotoUpload}
              onClose={() => setShowPhotoUpload(false)}
            />
          )}

          {/* Statistics Section */}
          {!isEditMode && (
            <div className="mt-8">
              <ProfileStatistics
                statistics={statistics}
                role={profile?.role}
              />
            </div>
          )}

          {/* Reviews Section */}
          {!isEditMode && reviews?.length > 0 && (
            <div className="mt-8">
              <ProfileReviews
                reviews={reviews}
                averageRating={statistics?.averageRating}
                totalReviews={statistics?.totalReviews}
              />
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PublicUserProfile;